import { useRef, useState, useEffect } from 'react';
import { Blurhash } from 'react-blurhash';
import { motion } from 'framer-motion';
import { isMobile } from 'react-device-detect';
import YouTube from 'react-youtube';
import Image from 'next/image';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from '../../styles/modules/object/ObjectGallery.module.scss';

const getYouTubeID = (l) => {
  const url = new URL(l);
  return url.searchParams.get('v');
};

const ObjectGallery = ({ images, video, raw }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [slidePosition, setSlidePosition] = useState(0);
  const [loadedImages, setLoadedImages] = useState([]);
  const { width } = useWindowDimensions();
  const galleryEl = useRef();
  const galleryThumbsRef = useRef();

  const handleThumbClick = (i) => {
    if (galleryThumbsRef.current) {
      const galleryThumbsWidth = galleryThumbsRef.current.offsetWidth;
      const galleryThumbsHeight = galleryThumbsRef.current.offsetHeight;

      galleryThumbsRef.current.scrollTo({
        top: 72 * i - galleryThumbsHeight / 2 + 30,
        left: 72 * i - galleryThumbsWidth / 2 + 30,
        behavior: 'smooth'
      });
    }

    const galleryWidth = galleryEl.current.offsetWidth;
    galleryEl.current.scrollTo({
      left: galleryWidth * (i + 1) - galleryWidth,
      behavior: 'smooth'
    });
  };
  const handlePreviousClick = (i) => {
    if (galleryThumbsRef.current) {
      const galleryThumbsWidth = galleryThumbsRef.current.offsetWidth;
      const galleryThumbsHeight = galleryThumbsRef.current.offsetHeight;

      galleryThumbsRef.current.scrollTo({
        top: 72 * (i - 1) - galleryThumbsHeight / 2 + 30,
        left: 72 * (i - 1) - galleryThumbsWidth / 2 + 30,
        behavior: 'smooth'
      });
    }

    const galleryWidth = galleryEl.current.offsetWidth;
    galleryEl.current.scrollTo({
      left: galleryWidth * (i - 1),
      behavior: 'smooth'
    });
  };
  const handleNextClick = (i) => {
    if (galleryThumbsRef.current) {
      const galleryThumbsWidth = galleryThumbsRef.current.offsetWidth;
      const galleryThumbsHeight = galleryThumbsRef.current.offsetHeight;

      galleryThumbsRef.current.scrollTo({
        top: 72 * (i + 1) - galleryThumbsHeight / 2 + 30,
        left: 72 * (i + 1) - galleryThumbsWidth / 2 + 30,
        behavior: 'smooth'
      });
    }

    const galleryWidth = galleryEl.current.offsetWidth;
    galleryEl.current.scrollTo({
      left: galleryWidth * (i + 1),
      behavior: 'smooth'
    });
  };
  const handleFullscreenClick = () => {
    setIsFullscreen(!isFullscreen);
  };
  const handleKeyboardPress = (e) => {
    if (e.keyCode === 27) setIsFullscreen(false);

    setSlidePosition((x) => {
      if (e.keyCode === 37) handlePreviousClick(x);
      if (e.keyCode === 39) handleNextClick(x);

      return x;
    });
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardPress);
    return () => document.removeEventListener('keydown', handleKeyboardPress);
  }, []);

  return (
    <div
      className={[styles.galleryWrapper, isFullscreen && styles.fullscreen]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.galleryLabels}>
        {!!raw.object.exclusive && (
          <div className={[styles.galleryLabelsItem, styles.red].join(' ')}>Только в ЛЕНДЛОРД</div>
        )}
        {!!raw.price.hersonCert && (
          <div className={[styles.galleryLabelsItem, styles.blue].join(' ')}>
            Херсонский сертификат
          </div>
        )}
        {!!raw.price.noCommission && (
          <div className={[styles.galleryLabelsItem, styles.yellow].join(' ')}>Без комиссии</div>
        )}
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: slidePosition >= (video ? 1 : 0) ? 1 : 0 }}
        className={[styles.galleryButton, styles.full].filter(Boolean).join(' ')}
        onClick={handleFullscreenClick}
      >
        {isFullscreen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : !isMobile ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        ) : null}
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: slidePosition > 0 ? 1 : 0 }}
        className={[styles.galleryButton, styles.left].filter(Boolean).join(' ')}
        onClick={() => handlePreviousClick(slidePosition)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: slidePosition < (video ? images.length : images.length - 1) ? 1 : 0 }}
        className={[styles.galleryButton, styles.right].filter(Boolean).join(' ')}
        onClick={() => handleNextClick(slidePosition)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </motion.div>
      <div
        className={styles.gallery}
        onScroll={(e) => {
          const selectedSlide = Math.round(e.target.scrollLeft / e.target.offsetWidth);
          if (selectedSlide === slidePosition) return;

          setSlidePosition(selectedSlide);
        }}
        onClick={() => {
          if (width < 992) {
            setIsFullscreen(true);
          }
        }}
        ref={galleryEl}
      >
        {video && (
          <div className={styles.gallerySlide}>
            <YouTube
              videoId={getYouTubeID(video)}
              opts={{
                width: '100%',
                height: '100%',
                playerVars: {
                  rel: 0,
                  modestbranding: 1,
                  frameborder: 0,
                  showinfo: 0
                }
              }}
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
            />
            <div
              style={{
                backgroundImage: `url(https://img.youtube.com/vi/${getYouTubeID(
                  video
                )}/maxres2.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '100%'
              }}
            />
          </div>
        )}
        {images.map((v, i) => (
          <div className={styles.gallerySlide} key={i}>
            {!isFullscreen && !!v.blurhash && (
              <Blurhash
                hash={v.blurhash}
                width="100%"
                height="100%"
                className={styles.gallerySlideHash}
              />
            )}
            {v.value && (
              <Image
                src={v.value}
                alt=""
                width={v.width}
                height={v.height}
                priority={video || (!video && i > 0) ? false : true}
                style={{ opacity: loadedImages.includes(v.value) ? 1 : 0 }}
                onLoad={() => setLoadedImages((x) => [...x, v.value])}
              />
            )}
          </div>
        ))}
      </div>
      {isFullscreen ? (
        <div className={styles.galleryInfo}>
          {slidePosition + 1} из {images.length}
        </div>
      ) : (
        <div className={styles.galleryThumbs} ref={galleryThumbsRef}>
          {video && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Фото"
              className={[styles.galleryThumb, slidePosition === 0 && styles.active]
                .filter(Boolean)
                .join(' ')}
              onClick={(e) => handleThumbClick(0)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ position: 'relative', left: 2 }}
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
          )}
          {images.map((v, i) => (
            <div
              role="button"
              tabIndex={0}
              aria-label="Фото"
              key={video ? i + 1 : i}
              className={[
                styles.galleryThumb,
                slidePosition === (video ? i + 1 : i) && styles.active
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleThumbClick(video ? i + 1 : i)}
            >
              {!!v.blurhash && (
                <Blurhash
                  hash={v.blurhash}
                  width="100%"
                  height="100%"
                  className={styles.galleryThumbHash}
                />
              )}
              {!!v.value && (
                <Image
                  src={v.value}
                  alt=""
                  width={v.width}
                  height={v.height}
                  style={{ opacity: loadedImages.includes(v.value) ? 1 : 0 }}
                  onLoad={() => setLoadedImages((x) => [...x, v.value])}
                />
              )}
            </div>
          ))}
          <div style={{ width: 0.1, flexShrink: 0, display: isMobile ? 'block' : 'none' }} />
        </div>
      )}
    </div>
  );
};

export default ObjectGallery;
